import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, FolderKanban, Search, ArrowRight, Loader2, Upload } from 'lucide-react';
import ProjectFormModal from '../components/ProjectFormModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HealthBadge = ({ score }) => {
  if (score === null || score === undefined) return null;
  const color = score >= 80 ? 'bg-green-100 text-green-800 border-green-300' :
                score >= 60 ? 'bg-amber-100 text-amber-800 border-amber-300' :
                'bg-red-100 text-red-800 border-red-300';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full border ${color}`} data-testid="health-score-badge">
      {score}
    </span>
  );
};

const ProjectsPage = () => {
  const { token, user } = useAuth();
  const canCreateProjects = !user || user.is_primary || user.allowed_projects === null || user.allowed_projects === undefined;
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [healthScores, setHealthScores] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  const fetchProjects = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [projRes, scoresRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers }),
        axios.get(`${API}/projects/health-scores`, { headers }).catch(() => ({ data: {} }))
      ]);
      setProjects(projRes.data);
      setHealthScores(scoresRes.data || {});
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [token]);

  const handleCreate = () => {
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    fetchProjects();
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON export file');
      return;
    }
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.export_version || !data.project) {
        toast.error('Invalid export file. Please use a file exported from MySpecWorkspace.');
        return;
      }
      const res = await axios.post(`${API}/projects/import`, data, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const imp = res.data.imported;
      toast.success(`Imported "${res.data.title}" with ${imp.dml_entries} data modules, ${imp.delivery_comparisons} comparisons, ${imp.brex_validations} validations`);
      fetchProjects();
      navigate(`/dashboard/projects/${res.data.id}`);
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error('Invalid JSON file');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to import project');
      }
    } finally {
      setImporting(false);
    }
  };

  const filteredProjects = projects.filter(p => 
    p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.capability?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8" data-testid="projects-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1">
          <h1 
            className="text-3xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Projects
          </h1>
          <p className="text-slate-600">
            Manage and organize your S1000D documentation projects
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canCreateProjects && (
            <>
          <input
            type="file"
            ref={importInputRef}
            accept=".json"
            className="hidden"
            onChange={handleImport}
            data-testid="import-file-input"
          />
          <Button
            variant="outline"
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
            className="text-violet-600 border-violet-200 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-300 flex items-center gap-2"
            data-testid="import-project-btn"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {importing ? 'Importing...' : 'Import Project'}
          </Button>
          <Button 
            onClick={handleCreate}
            className="bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-2"
            data-testid="create-project-btn"
          >
            <Plus className="w-4 h-4" />
            Create New Project
          </Button>
            </>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input 
          placeholder="Search projects..." 
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-projects-input"
        />
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      ) : filteredProjects.length === 0 ? (
        /* Empty State */
        <Card className="border-dashed border-2 border-slate-200 bg-slate-50/50">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-6">
              <FolderKanban className="w-10 h-10 text-slate-400" />
            </div>
            <CardTitle 
              className="text-xl text-slate-700 mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {searchTerm ? 'No Projects Found' : 'No Projects Yet'}
            </CardTitle>
            <CardDescription className="max-w-sm mb-6">
              {searchTerm 
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first S1000D project. Projects help you organize documentation, manage deliveries, and validate data modules.'
              }
            </CardDescription>
            {!searchTerm && canCreateProjects && (
              <Button 
                onClick={handleCreate}
                className="bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-2"
                data-testid="create-first-project-btn"
              >
                <Plus className="w-4 h-4" />
                Create Your First Project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Projects Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Card 
              key={project.id} 
              className="group bg-white border border-slate-200 hover:border-violet-200 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer"
              onClick={() => navigate(`/dashboard/projects/${project.id}`)}
              data-testid={`project-card-${project.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle 
                    className="text-lg line-clamp-1"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {project.title}
                  </CardTitle>
                  <div className="flex items-center gap-2 shrink-0 mt-0.5">
                    <HealthBadge score={healthScores[project.id]} />
                    <ArrowRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:text-violet-600 transition-all" />
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <Badge variant="secondary" className="text-xs">
                    {project.s1000d_version}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-3 line-clamp-1">
                  {project.capability}
                </p>
                {project.description && (
                  <p className="text-sm text-slate-600 line-clamp-2">
                    {project.description}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Project Form Modal — Create Only */}
      <ProjectFormModal
        open={showModal}
        onOpenChange={setShowModal}
        project={null}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default ProjectsPage;
